import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, isAdmin } = useAuth();
  const [userType, setUserType] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');

  // -------------------------------------------------------------------------
  // Scroll handling
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== 'undefined') {
        setScrolled(window.scrollY > 50);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Fetch user profile (user_type, display_name)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!user) {
      setUserType(null);
      setDisplayName('');
      return;
    }
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('user_type, display_name')
        .eq('id', user.id)
        .maybeSingle();
      if (data) {
        setUserType(data.user_type);
        setDisplayName(data.display_name || '');
      }
    };
    fetchProfile();
  }, [user]);

  // -------------------------------------------------------------------------
  // Account button click
  // -------------------------------------------------------------------------
  const handleAccountClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowUserMenu(!showUserMenu);
  };

  // -------------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------------
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setShowUserMenu(false);
      setUserType(null);
      setDisplayName('');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/90 backdrop-blur-xl border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.5)]'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 sm:px-8 flex items-center justify-between h-16 sm:h-18">
        {/* Left - Logo / Home */}
        <Link
          to="/"
          className="flex items-center gap-2 text-white hover:text-neon-green transition-colors duration-300 cursor-pointer"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-full border border-white/20 hover:border-neon-green transition-all duration-300">
            <i className="ri-music-2-line text-lg"></i>
          </div>
          <span className="hidden sm:inline text-sm tracking-widest font-bold">DigDim</span>
        </Link>

        {/* Center - Nav Links (Desktop) */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'Makers', href: '#makers', icon: 'ri-music-2-line' },
            { label: 'Seekers', href: '#seekers', icon: 'ri-search-line' },
            { label: 'Experience', href: '#experience', icon: 'ri-headphone-line' },
            { label: 'NFC', href: '#nfc-redirect', icon: 'ri-nfc-line' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="text-gray-400 text-sm tracking-wider hover:text-neon-green transition-colors duration-300 cursor-pointer flex items-center gap-2"
            >
              <i className={`${item.icon} text-base`}></i>
              {item.label}
            </a>
          ))}
        </div>

        {/* Right - Action Buttons */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Admin Button — 管理者のみ表示 */}
          {!loading && isAdmin && (
            <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 text-gray-400 hover:border-neon-green hover:text-neon-green hover:bg-neon-green/10 hover:shadow-[0_0_20px_rgba(0,255,65,0.2)] transition-all duration-300 cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-settings-3-line text-sm"></i>
              </div>
              <span className="hidden sm:inline text-xs tracking-wider font-medium">ADMIN</span>
            </Link>
          )}

          {/* Mobile Nav Icons */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href="#makers"
              className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-neon-green hover:bg-white/5 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-music-2-line text-lg"></i>
            </a>
          </div>

          {/* User Menu */}
          {!loading && (
            <div className="relative">
              {user ? (
                <>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="whitespace-nowrap w-10 h-10 flex items-center justify-center rounded-full border border-neon-green bg-neon-green/10 text-neon-green shadow-[0_0_20px_rgba(0,255,65,0.3)] transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-user-line text-lg"></i>
                  </button>

                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowUserMenu(false)}
                      />
                      <div
                        className="absolute top-full right-0 mt-3 w-64 bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50"
                        style={{ boxShadow: '0 0 40px rgba(0, 255, 65, 0.15)' }}
                      >
                        <div className="p-4 border-b border-white/10">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-neon-green/20 border border-neon-green/40">
                              <i className="ri-user-line text-lg text-neon-green"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">
                                {displayName || user.email}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {isAdmin ? '管理者' : userType === 'maker' ? 'Maker' : userType === 'seeker' ? 'Seeker' : 'ログイン中'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="p-2">
                          <Link
                            to="/profile-edit"
                            onClick={() => setShowUserMenu(false)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-neon-green hover:bg-neon-green/10 transition-all duration-300 cursor-pointer text-sm"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-edit-line text-base"></i>
                            </div>
                            プロフィール編集
                          </Link>
                          {userType === 'maker' && (
                            <Link
                              to="/layer-edit"
                              onClick={() => setShowUserMenu(false)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-300 cursor-pointer text-sm"
                            >
                              <div className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-stack-line text-base"></i>
                              </div>
                              レイヤー編集
                            </Link>
                          )}
                          {isAdmin && (
                            <Link
                              to="/admin"
                              onClick={() => setShowUserMenu(false)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-neon-green hover:bg-neon-green/10 transition-all duration-300 cursor-pointer text-sm"
                            >
                              <div className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-settings-3-line text-base"></i>
                              </div>
                              管理者パネル
                            </Link>
                          )}
                          <div className="my-1 h-px bg-white/5" />
                          <button
                            onClick={async () => {
                              await supabase.auth.signOut();
                              setShowUserMenu(false);
                              setUserType(null);
                              setDisplayName('');
                            }}
                            className="whitespace-nowrap w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-300 cursor-pointer text-sm"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-logout-box-line text-base"></i>
                            </div>
                            ログアウト
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-neon-green hover:text-neon-green hover:shadow-[0_0_20px_rgba(0,255,65,0.3)] transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-user-line text-lg"></i>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
